import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import loadCustomerOptions from './shared/loadCustomerOptions';
import DateInput from '../../shared/DateInput';
import FilterDrawer from '../../shared/FilterDrawer';
import {
  useElementWidth, topRoundedBar, SegmentedToggle, ChartHoverBox,
  MAX_BAR_WIDTH, STACK_GAP, BASELINE_COLOR, HIGHLIGHT_COLOR, loadingFade,
} from '../../shared/DashboardChart';
import { todayInTenant, formatDate, formatDateWords, formatMonthName } from '../../shared/tenantClock';
import { formatMoney, formatNumber } from '../../shared/numberFormat';
import '../../shared/StyledInput.css';

// Sales Dashboard's Sales by Period chart: every submitted Sales Order by
// its Order Date (GET /api/sales-orders/sales-by-period), as Sales ex GST
// or a count of orders, grouped by Day / Week / Month / Quarter over a
// preset or custom date range. Pick up to 8 Customers to see one series
// each; with none picked it's every Customer combined. Clicking a period
// opens its orders in a side panel, same as the CRM Dashboard's Activities.
// Drawn with the shared dashboard chart pieces (shared/DashboardChart.js).

const CARD_STYLE = { background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', boxShadow: 'var(--shadow-sm)', padding: 16 };
const EMPTY_STYLE = { fontSize: 14, color: '#888' };
const LABEL_STYLE = { fontSize: 12, color: '#666' };
// The plot's height; the period labels sit below it.
const PLOT_HEIGHT = 180;
const DRILL_PAGE_SIZE = 25;
const DRILL_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', border: '1px solid #e3e7ee', borderRadius: 6, fontSize: 13, cursor: 'pointer', minWidth: 0 };
// Same look as DashboardCalendar.js's prev/next buttons.
const navBtnStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
  border: '1px solid #b3bbc7', background: '#fff', borderRadius: 4, padding: 0,
  cursor: disabled ? 'default' : 'pointer', color: disabled ? '#b0b6c0' : '#5a6b85',
});

// One series: the app's accent blue. Customers take the categorical hues
// below in this fixed order (validated for colour-blind separation); a
// Customer keeps its colour while others are added or removed.
const SINGLE_COLOR = '#1976d2';
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const MAX_CUSTOMERS = SERIES_COLORS.length;

const MEASURES = [
  { key: 'value', label: 'Sales', title: 'Show sales value ex GST' },
  { key: 'orders', label: 'Orders', title: 'Show the number of orders' },
];
const CHART_TYPES = [
  { key: 'column', label: 'Column', title: 'Show a column per Customer side by side' },
  { key: 'stacked', label: 'Stacked', title: 'Stack Customers into one column per period' },
  { key: 'line', label: 'Line', title: 'Show a line per Customer' },
  { key: 'area', label: 'Area', title: 'Stack Customers as shaded areas' },
];
const GROUPS = [
  { key: 'day', label: 'Day', days: 1 },
  { key: 'week', label: 'Week', days: 7 },
  { key: 'month', label: 'Month', days: 30.4 },
  { key: 'quarter', label: 'Quarter', days: 91.3 },
];
// Above this many bars a grouping is too fine to read, so it's greyed out.
const MAX_BUCKETS = 370;

// ---- date maths on 'YYYY-MM-DD' strings, in UTC so no zone shifts a day ----

function toDate(ymd) { return new Date(`${ymd}T00:00:00Z`); }
function toYmd(d) { return d.toISOString().slice(0, 10); }
function addDays(ymd, days) { const d = toDate(ymd); d.setUTCDate(d.getUTCDate() + days); return toYmd(d); }
function addMonths(ymd, months) { const d = toDate(ymd); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + months); return toYmd(d); }
function monthStart(ymd) { return `${ymd.slice(0, 7)}-01`; }
function daysBetween(from, to) { return Math.round((toDate(to) - toDate(from)) / 86400000) + 1; }

// The start of the Day / Week (Monday) / Month / Quarter a date falls in,
// matching the backend's date_trunc.
function bucketStart(ymd, group) {
  if (group === 'day') return ymd;
  if (group === 'week') { const dow = (toDate(ymd).getUTCDay() + 6) % 7; return addDays(ymd, -dow); }
  if (group === 'month') return monthStart(ymd);
  const m = Number(ymd.slice(5, 7));
  return `${ymd.slice(0, 4)}-${String((Math.floor((m - 1) / 3) * 3) + 1).padStart(2, '0')}-01`;
}

function nextBucket(ymd, group) {
  if (group === 'day') return addDays(ymd, 1);
  if (group === 'week') return addDays(ymd, 7);
  return addMonths(ymd, group === 'month' ? 1 : 3);
}

function bucketsFor(from, to, group) {
  const out = [];
  for (let b = bucketStart(from, group); b <= to && out.length <= MAX_BUCKETS; b = nextBucket(b, group)) out.push(b);
  return out;
}

function quarterLabel(start, yearOpt) {
  const end = addMonths(start, 2);
  return `${formatMonthName(start)}–${formatMonthName(end)} ${formatDateWords(start, { year: yearOpt })}`;
}

// The short label under the axis, and the full one in the hover box.
function axisLabel(b, group) {
  if (group === 'month') return formatDateWords(b, { month: 'short', year: '2-digit' });
  if (group === 'quarter') return quarterLabel(b, '2-digit');
  return formatDateWords(b, { day: 'numeric', month: 'short' });
}

function fullLabel(b, group) {
  if (group === 'day') return formatDateWords(b, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  if (group === 'week') return `Week of ${formatDateWords(b)}`;
  if (group === 'month') return formatDateWords(b, { month: 'long', year: 'numeric' });
  return quarterLabel(b, 'numeric');
}

// Financial year starts 1 July (Australia).
function financialYearStart(ymd) {
  const y = Number(ymd.slice(0, 4));
  return Number(ymd.slice(5, 7)) >= 7 ? `${y}-07-01` : `${y - 1}-07-01`;
}

const PRESETS = [
  { key: 'this_month', label: 'This month', range: (t) => [monthStart(t), t] },
  { key: 'last_3', label: 'Last 3 months', range: (t) => [addMonths(t, -2), t] },
  { key: 'last_6', label: 'Last 6 months', range: (t) => [addMonths(t, -5), t] },
  { key: 'last_12', label: 'Last 12 months', range: (t) => [addMonths(t, -11), t] },
  { key: 'this_fy', label: 'This financial year', range: (t) => [financialYearStart(t), t] },
  { key: 'last_fy', label: 'Last financial year', range: (t) => { const s = addMonths(financialYearStart(t), -12); return [s, addDays(addMonths(s, 12), -1)]; } },
  { key: 'this_year', label: 'This calendar year', range: (t) => [`${t.slice(0, 4)}-01-01`, t] },
  { key: 'custom', label: 'Custom', range: null },
];

// The range one step back (-1) or forward (+1). A range starting on the 1st
// moves by the whole months it covers (a year for This financial / calendar
// year) and lands on full months; any other range moves by its own length.
function shiftRange(from, to, preset, dir) {
  if (from.slice(8) === '01') {
    const covered = ((Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12) + Number(to.slice(5, 7)) - Number(from.slice(5, 7)) + 1;
    const months = preset === 'this_fy' || preset === 'this_year' ? 12 : covered;
    const f = addMonths(from, dir * months);
    return [f, addDays(addMonths(f, months), -1)];
  }
  const days = daysBetween(from, to) * dir;
  return [addDays(from, days), addDays(to, days)];
}

// The grouping that suits a range: about a dozen-plus bars, never too many.
function defaultGroup(from, to) {
  const days = daysBetween(from, to);
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  if (days <= 1100) return 'month';
  return 'quarter';
}

// A round axis top and step: 0, step, 2*step... at or above max.
function niceScale(max) {
  if (max <= 0) return { top: 1, step: 0.25 };
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw);
  return { top: Math.ceil(max / step) * step, step };
}

function formatOrders(n) {
  return `${formatNumber(n, { decimals: 0 })} ${n === 1 ? 'order' : 'orders'}`;
}

const customerSelectStyles = {
  container: (base) => ({ ...base, flex: '1 1 320px', minWidth: 0 }),
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : '#a9a9a9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
    fontSize: 13,
    '&:hover': { borderColor: state.isFocused ? '#1976d2' : '#a9a9a9' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 13 }),
  multiValue: (base, { data }) => ({ ...base, background: '#f1f4f8', borderLeft: `3px solid ${SERIES_COLORS[data.slot]}` }),
  menuPortal: (base) => ({ ...base, zIndex: 1000 }),
};

// The plot: columns (side by side or stacked), lines, or stacked areas, with
// a y-axis of round values. Hovering a period highlights it and shows each
// series' figure, plus its order count when showing sales value (ordersAt).
function SalesChart({ buckets, group, series, valueAt, ordersAt, chartType, formatAxis, formatValue, onPeriodClick, selected }) {
  const [ref, width] = useElementWidth();
  const [hover, setHover] = useState(null);
  const stacked = chartType === 'stacked' || chartType === 'area';

  const totals = buckets.map((b) => series.reduce((sum, s) => sum + valueAt(s.key, b), 0));
  const max = stacked
    ? Math.max(0, ...totals)
    : Math.max(0, ...buckets.flatMap((b) => series.map((s) => valueAt(s.key, b))));
  const { top, step } = niceScale(max);
  const ticks = [];
  for (let v = 0; v <= top + (step / 2); v += step) ticks.push(v);

  const tickLabels = ticks.map(formatAxis);
  const left = Math.max(...tickLabels.map((t) => t.length)) * 6.5 + 10;
  const plotTop = 10;
  const plotH = PLOT_HEIGHT;
  const labelArea = 22;
  const plotW = Math.max(0, width - left - 4);
  const bandW = buckets.length ? plotW / buckets.length : 0;
  const y = (v) => plotTop + plotH - ((v / top) * plotH);
  const cx = (i) => left + (i * bandW) + (bandW / 2);
  const labelEvery = Math.max(1, Math.ceil((group === 'quarter' ? 64 : 46) / Math.max(bandW, 1)));

  // Per series, its bottom and top at each bucket (bottom is 0 unless stacked).
  const layers = useMemo(() => {
    const base = buckets.map(() => 0);
    return series.map((s) => {
      const pts = buckets.map((b, i) => {
        const v = valueAt(s.key, b);
        const lo = stacked ? base[i] : 0;
        if (stacked) base[i] += v;
        return { v, lo, hi: lo + v };
      });
      return { ...s, pts };
    });
  }, [buckets, series, valueAt, stacked]);

  let marks = null;
  if (bandW > 0) {
    if (chartType === 'column') {
      const groupW = Math.min(bandW * 0.72, Math.max(MAX_BAR_WIDTH, 28 * series.length + 2 * (series.length - 1)));
      const gap = series.length > 1 && groupW / series.length > 6 ? 2 : 0;
      const barW = Math.max(1, (groupW - (gap * (series.length - 1))) / series.length);
      marks = layers.map((s, si) => s.pts.map((p, i) => {
        if (p.v <= 0) return null;
        const x = cx(i) - (groupW / 2) + (si * (barW + gap));
        return <path key={`${s.key}-${i}`} d={topRoundedBar(x, y(p.v), barW, y(0) - y(p.v))} fill={s.color} />;
      }));
    } else if (chartType === 'stacked') {
      const barW = Math.max(1, Math.min(bandW * 0.62, MAX_BAR_WIDTH));
      marks = layers.map((s, si) => s.pts.map((p, i) => {
        if (p.v <= 0) return null;
        const isTop = !layers.slice(si + 1).some((upper) => upper.pts[i].v > 0);
        const x = cx(i) - (barW / 2);
        // A white gap between stacked segments.
        const h = Math.max(1, y(p.lo) - y(p.hi) - (si > 0 && p.lo > 0 ? STACK_GAP : 0));
        const yTop = y(p.hi);
        return isTop
          ? <path key={`${s.key}-${i}`} d={topRoundedBar(x, yTop, barW, h)} fill={s.color} />
          : <rect key={`${s.key}-${i}`} x={x} y={yTop} width={barW} height={h} fill={s.color} />;
      }));
    } else {
      const showDots = bandW >= 10;
      marks = layers.map((s) => {
        const line = s.pts.map((p, i) => `${i ? 'L' : 'M'}${cx(i)},${y(p.hi)}`).join(' ');
        const area = chartType === 'area'
          ? `${line} ${[...s.pts].reverse().map((p, ri) => `L${cx(s.pts.length - 1 - ri)},${y(p.lo)}`).join(' ')} Z`
          : null;
        return (
          <g key={s.key}>
            {area && <path d={area} fill={s.color} fillOpacity={0.28} />}
            <path d={line} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
            {showDots && s.pts.map((p, i) => (
              <circle key={i} cx={cx(i)} cy={y(p.hi)} r={hover === i ? 5 : 3.5} fill={s.color} stroke="#fff" strokeWidth={2} />
            ))}
          </g>
        );
      });
    }
  }

  const tooltip = hover !== null && buckets[hover] ? (
    <ChartHoverBox
      chartWidth={width}
      anchorLeft={left + (hover * bandW)}
      anchorRight={left + ((hover + 1) * bandW)}
      top={plotTop}
      heading={fullLabel(buckets[hover], group)}
      rows={[...series].reverse().map((s) => ({ key: s.key, color: s.color, name: series.length > 1 ? s.name : '', value: formatValue(valueAt(s.key, buckets[hover])), detail: ordersAt && formatOrders(ordersAt(s.key, buckets[hover])) }))}
      total={series.length > 1 ? formatValue(totals[hover]) : undefined}
      totalDetail={ordersAt && series.length > 1 ? formatOrders(series.reduce((sum, s) => sum + ordersAt(s.key, buckets[hover]), 0)) : undefined}
      hint="Click to see these orders"
    />
  ) : null;
  // The drilled-into period stays highlighted while its panel is open.
  const highlighted = [...new Set([hover, buckets.indexOf(selected)])].filter((i) => i !== null && i >= 0);

  return (
    <div ref={ref} style={{ width: '100%', position: 'relative' }} onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={plotTop + plotH + labelArea} role="img" aria-label="Sales by period">
          {ticks.map((t, i) => (
            <g key={t}>
              <line x1={left} x2={width - 4} y1={y(t)} y2={y(t)} stroke={i === 0 ? BASELINE_COLOR : '#eef1f5'} />
              <text x={left - 8} y={y(t) + 3.5} textAnchor="end" fontSize={10} fill="#666">{tickLabels[i]}</text>
            </g>
          ))}
          {highlighted.map((i) => (
            <rect key={`hl-${i}`} x={left + (i * bandW)} y={plotTop} width={bandW} height={plotH} fill={HIGHLIGHT_COLOR} />
          ))}
          {hover !== null && (chartType === 'line' || chartType === 'area') && (
            <line x1={cx(hover)} x2={cx(hover)} y1={plotTop} y2={plotTop + plotH} stroke="#9aa5b5" strokeDasharray="3 3" />
          )}
          {marks}
          {buckets.map((b, i) => (i % labelEvery === 0) && (
            <text key={b} x={cx(i)} y={plotTop + plotH + 15} textAnchor="middle" fontSize={10} fill="#666">{axisLabel(b, group)}</text>
          ))}
          {/* Hit areas: the whole height of each period, bigger than any mark. */}
          {buckets.map((b, i) => (
            <rect
              key={`hit-${b}`}
              x={left + (i * bandW)}
              y={0}
              width={bandW}
              height={plotTop + plotH + labelArea}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)}
              onClick={() => onPeriodClick(b)}
            />
          ))}
        </svg>
      )}
      {tooltip}
    </div>
  );
}

// The drill-down behind a clicked period, in a right-side panel (same as
// the CRM Dashboard's Activities): that period's orders for any picked
// Customers, newest first, with the same total as the column. Load more
// brings in older ones; Open in Sales Orders shows them on the full list.
// drill: { from, to, label, customers: [{ value, label }] }.
function SalesPeriodDrawer({ drill, onClose }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const customerIds = (drill?.customers || []).map((c) => c.value).join(',');

  const load = (nextPage, append) => {
    setLoading(true);
    axios.get('/api/sales-orders', {
      params: { date_from: drill.from, date_to: drill.to, customer_ids: customerIds || undefined, sortBy: 'order_date', sortOrder: 'DESC', page: nextPage, limit: DRILL_PAGE_SIZE },
    })
      .then((res) => {
        const data = res.data.data || [];
        setRows((prev) => (append ? [...prev, ...data] : data));
        setPagination(res.data.pagination || null);
        setSummary(res.data.summary || null);
        setPage(nextPage);
      })
      .catch(() => { if (!append) { setRows([]); setPagination(null); setSummary(null); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!drill) return;
    setRows([]);
    setSummary(null);
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill]);

  const openList = () => navigate('/sales-orders', { state: { initialFilters: { date_from: drill.from, date_to: drill.to, customers: drill.customers } } });
  const customerNames = (drill?.customers || []).map((c) => c.label).join(', ');

  let content;
  if (loading && !rows.length) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else if (rows.length === 0) content = <div style={EMPTY_STYLE}>No orders for {drill?.label}.</div>;
  else {
    content = (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((o) => (
            <div
              key={o.id}
              className="list-row"
              onClick={() => navigate(`/sales-orders/${o.id}`)}
              title={`Open ${o.display_number}`}
              style={DRILL_ROW_STYLE}
            >
              <span style={{ width: 84, flexShrink: 0, color: '#666' }}>{formatDate(o.order_date)}</span>
              <span style={{ width: 110, flexShrink: 0, fontWeight: 600 }}>{o.display_number}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.customer?.name}</span>
              <span style={{ flexShrink: 0, fontWeight: 600 }}>{formatMoney(o.total_amount)}</span>
            </div>
          ))}
        </div>
        {pagination && rows.length < pagination.total && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button type="button" className="dashboard-refresh-btn" disabled={loading} onClick={() => load(page + 1, true)} title="Load the next older orders">
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <FilterDrawer
      open={!!drill}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      title={`Sales Orders · ${drill?.label || ''}`}
      description={customerNames ? `For ${customerNames}. Click one to open it.` : 'Every customer. Click one to open it.'}
      width="min(640px, 96vw)"
      footer={(
        <>
          <span />
          <button type="button" onClick={openList} title="Show these orders on the Sales Orders list" style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            Open in Sales Orders
          </button>
        </>
      )}
    >
      {summary && (
        <div style={{ ...LABEL_STYLE, display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 12 }}>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatMoney(summary.value)}</strong> sales ex GST</span>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatNumber(summary.orders, { decimals: 0 })}</strong> orders</span>
        </div>
      )}
      {content}
    </FilterDrawer>
  );
}

function SalesByPeriodCard({ refreshKey }) {
  const initialRange = PRESETS.find((p) => p.key === 'last_12').range(todayInTenant());
  const [preset, setPreset] = useState('last_12');
  const [from, setFrom] = useState(initialRange[0]);
  const [to, setTo] = useState(initialRange[1]);
  const [group, setGroup] = useState('month');
  const [measure, setMeasure] = useState('value');
  const [chartType, setChartType] = useState('column');
  // Picked Customers, each with the colour slot it keeps while picked.
  const [customers, setCustomers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The open drill-down panel, or null when closed.
  const [drill, setDrill] = useState(null);

  const customerKey = customers.map((c) => c.value).join(',');

  useEffect(() => {
    if (!from || !to || from > to) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get('/api/sales-orders/sales-by-period', { params: { from, to, group, customer_ids: customerKey || undefined } })
      .then((res) => { if (!cancelled) setResult(res.data.data); })
      .catch((err) => { if (!cancelled) { setResult(null); setError(err.response?.data?.message || 'Could not load sales.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, group, customerKey, refreshKey]);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (!p.range) return;
    const [f, t] = p.range(todayInTenant());
    setFrom(f);
    setTo(t);
    setGroup(defaultGroup(f, t));
  };

  const changeDate = (which, value) => {
    if (!value) return;
    setPreset('custom');
    const f = which === 'from' ? value : from;
    const t = which === 'to' ? value : to;
    if (which === 'from') setFrom(value); else setTo(value);
    if (f <= t && bucketsFor(f, t, group).length > MAX_BUCKETS) setGroup(defaultGroup(f, t));
  };

  const stepRange = (dir) => {
    const [f, t] = shiftRange(from, to, preset, dir);
    setPreset('custom');
    setFrom(f);
    setTo(t);
    if (bucketsFor(f, t, group).length > MAX_BUCKETS) setGroup(defaultGroup(f, t));
  };
  const canStep = from && to && from <= to;

  // Drill-down: the clicked period (trimmed to the chart's range, as a week
  // or quarter can start before it) and any picked Customers — the same
  // orders the column counts.
  const openPeriod = (bucket) => {
    const dateFrom = bucket < result.from ? result.from : bucket;
    const end = addDays(nextBucket(bucket, result.group), -1);
    const dateTo = end > result.to ? result.to : end;
    setDrill({
      bucket,
      from: dateFrom,
      to: dateTo,
      label: fullLabel(bucket, result.group),
      customers: customers.map(({ value, label }) => ({ value, label })),
    });
  };

  const handleCustomersChange = (picked) => {
    const next = picked || [];
    const used = new Set(customers.filter((c) => next.some((n) => n.value === c.value)).map((c) => c.slot));
    setCustomers(next.map((n) => {
      const kept = customers.find((c) => c.value === n.value);
      if (kept) return kept;
      const slot = SERIES_COLORS.findIndex((_, i) => !used.has(i));
      used.add(slot);
      return { ...n, slot };
    }));
  };

  const rangeDays = from && to && from <= to ? daysBetween(from, to) : 0;
  const buckets = useMemo(() => (result ? bucketsFor(result.from, result.to, result.group) : []), [result]);
  const lookup = useMemo(() => {
    const m = new Map();
    (result?.rows || []).forEach((r) => m.set(`${r.series}|${r.bucket}`, r));
    return m;
  }, [result]);
  const valueAt = useMemo(() => (seriesKey, bucket) => Number(lookup.get(`${seriesKey}|${bucket}`)?.[measure]) || 0, [lookup, measure]);
  const ordersAt = useMemo(() => (seriesKey, bucket) => Number(lookup.get(`${seriesKey}|${bucket}`)?.orders) || 0, [lookup]);

  const series = useMemo(() => (result?.series || []).map((s) => {
    const picked = customers.find((c) => String(c.value) === s.key);
    return { ...s, color: picked ? SERIES_COLORS[picked.slot] : SINGLE_COLOR };
  }), [result, customers]);

  const sumOf = (key) => (result?.rows || []).reduce((sum, r) => sum + (Number(r[key]) || 0), 0);
  const seriesTotal = (s) => buckets.reduce((sum, b) => sum + valueAt(s.key, b), 0);

  const formatValue = measure === 'value' ? (v) => formatMoney(v) : (v) => formatNumber(v, { decimals: 0 });
  const formatAxis = measure === 'value' ? (v) => formatMoney(v, { decimals: 0 }) : (v) => formatNumber(v, { maxDecimals: 2 });

  let content;
  if (from > to) content = <div style={EMPTY_STYLE}>From must be on or before To.</div>;
  else if (error) content = <div style={EMPTY_STYLE}>{error}</div>;
  else if (!result) content = <div style={EMPTY_STYLE}>Loading...</div>;
  else {
    content = (
      <div style={loadingFade(loading)}>
        <div style={{ ...LABEL_STYLE, display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatMoney(sumOf('value'))}</strong> sales ex GST</span>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatNumber(sumOf('orders'), { decimals: 0 })}</strong> orders</span>
          <span>{formatDateWords(result.from)} – {formatDateWords(result.to)}</span>
          {series.length > 1 && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {series.map((s) => (
                <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#444' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
                  {s.name} <strong style={{ color: 'var(--text-dark)' }}>{formatValue(seriesTotal(s))}</strong>
                </span>
              ))}
            </span>
          )}
        </div>
        <SalesChart
          buckets={buckets}
          group={result.group}
          series={series}
          valueAt={valueAt}
          ordersAt={measure === 'value' ? ordersAt : undefined}
          chartType={chartType}
          formatAxis={formatAxis}
          formatValue={formatValue}
          onPeriodClick={openPeriod}
          selected={drill?.bucket}
        />
      </div>
    );
  }

  return (
    <div className="metrics-section">
      <h2>Sales by Period</h2>
      <div style={CARD_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <select
            className="styled-input"
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
            title="Pick a date range"
            style={{ fontSize: 13 }}
          >
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <button type="button" style={navBtnStyle(!canStep)} onClick={() => stepRange(-1)} disabled={!canStep} title="Show the previous period" aria-label="Previous period"><ChevronLeft size={16} /></button>
            <button type="button" style={navBtnStyle(!canStep)} onClick={() => stepRange(1)} disabled={!canStep} title="Show the next period" aria-label="Next period"><ChevronRight size={16} /></button>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={LABEL_STYLE}>From</span>
            <DateInput value={from} onChange={(e) => changeDate('from', e.target.value)} max={to} style={{ width: 150 }} />
            <span style={LABEL_STYLE}>To</span>
            <DateInput value={to} onChange={(e) => changeDate('to', e.target.value)} min={from} style={{ width: 150 }} />
          </span>
          <SegmentedToggle
            options={GROUPS.map((g) => ({ ...g, title: `Group by ${g.label.toLowerCase()}` }))}
            value={group}
            onChange={setGroup}
            isDisabled={(g) => rangeDays / g.days > MAX_BUCKETS}
            disabledTitle={(g) => `Too many ${g.label.toLowerCase()}s in this date range`}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            loadOptions={loadCustomerOptions}
            value={customers}
            onChange={handleCustomersChange}
            isOptionDisabled={() => customers.length >= MAX_CUSTOMERS}
            placeholder="All customers — pick up to 8 to compare..."
            noOptionsMessage={() => 'No matching customers'}
            loadingMessage={() => 'Searching...'}
            styles={customerSelectStyles}
            menuPortalTarget={document.body}
          />
          <SegmentedToggle options={MEASURES} value={measure} onChange={setMeasure} />
          <SegmentedToggle options={CHART_TYPES} value={chartType} onChange={setChartType} />
        </div>
        {content}
      </div>
      <SalesPeriodDrawer drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

export default SalesByPeriodCard;
